// In-galaxy screens: owned systems, system view with buildings and Trading Port, menu.
import type {
  FacilityType,
  FullSystem,
  Resources,
  ShipType,
  SystemResponse,
} from '@stars-and-ships/api-types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Shield } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useOutletContext, useParams, useSearchParams } from 'react-router';
import { endpoints } from '../api/endpoints';
import { invalidateSoon } from '../api/invalidate';
import { queryKeys, useAlliance, useCodex, useScouts, useSystem } from '../api/queries';
import { DiscordLink } from '../components/DiscordLink';
import { FeedbackSheet } from '../components/FeedbackSheet';
import type { GameContext } from '../components/GameLayout';
import { OwnerLinks } from '../components/ProfileLinks';
import { AlliedSystemView } from '../components/SystemViews';
import { ForeignActions } from '../components/system/ForeignActions';
import { ForeignSystemInfo, IntelReport } from '../components/system/ForeignSystem';
import { OwnSystemView } from '../components/system/OwnSystemView';
import { SystemSwitcher } from '../components/system/SystemSwitcher';
import { parseSystemTab, type SystemActions, type SystemTab } from '../components/system/types';
import { Button, ErrorNotice, Page, Panel, Spinner } from '../components/ui';
import { useChannelSubscription } from '../state/channels';
import { useNow } from '../state/live';
import { useSelectedSystem } from '../state/selectedSystem';
import { useSession } from '../state/session';
import {
  PENDING_ID_PREFIX,
  patchCachedSystem,
  predictPortTradeStarted,
  predictProductionCancelled,
  predictProductionStarted,
  predictUpgradeCancelled,
  predictUpgradeStarted,
  withoutPending,
  withPortTradeStarted,
  withProductionCancelled,
  withProductionStarted,
  withUpgradeCancelled,
  withUpgradeStarted,
} from '../state/systemCache';
import { useLogout } from '../state/useLogout';
import { VERSION_LABEL } from '../version';
import { SystemMilitaryBar, useSystemNames } from './fleet';

export function SystemsScreen() {
  const { galaxyId, me } = useOutletContext<GameContext>();
  const navigate = useNavigate();
  if (!me) return <Spinner />;
  return (
    <Page width="wide">
      <h1 className="font-display text-xl font-bold uppercase tracking-[0.08em] text-text">
        Your systems
      </h1>
      <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {me.systems.map((system, index) => (
          <li key={system.id}>
            <button
              type="button"
              className="system-card w-full hud-cut-sm border border-line bg-panel p-4 text-left hover:border-accent"
              onClick={() => navigate(`/g/${galaxyId}/systems/${system.id}`)}
            >
              <span className="block font-semibold text-text">{system.name}</span>
              <span className="text-xs text-muted">{index === 0 ? 'Home system' : 'Colony'}</span>
            </button>
          </li>
        ))}
      </ul>
    </Page>
  );
}

/** The cached system before a prediction, and the id of the provisional entry it added. */
interface OptimisticContext {
  snapshot: SystemResponse | undefined;
  pendingId: string;
}

/** setTimeout cannot wait longer than 2^31 − 1 ms. */
const MAX_TIMEOUT_MS = 2_147_483_647;

export function SystemScreen() {
  const { galaxyId, me } = useOutletContext<GameContext>();
  const { systemId = '' } = useParams();
  const select = useSelectedSystem((s) => s.select);
  useEffect(() => {
    if (systemId) select(galaxyId, systemId);
  }, [galaxyId, systemId, select]);
  const system = useSystem(galaxyId, systemId);
  const [params, setParams] = useSearchParams();
  const tab = parseSystemTab(params.get('tab'));
  const setTab = (next: SystemTab) => setParams(next === 'buildings' ? {} : { tab: next });
  const scouts = useScouts(galaxyId);
  const codex = useCodex(galaxyId);
  const queryClient = useQueryClient();
  const now = useNow();
  const full = system.data?.tier === 'full' ? system.data.system : undefined;
  // `full` on a system the player does not own = an alliance member's system (04 §3.3).
  const allied = full && me && full.owner?.playerId !== me.playerId ? full : undefined;
  const alliance = useAlliance(galaxyId, allied ? me?.alliance?.id : undefined);
  const memberName = (playerId: string | null) =>
    playerId === me?.playerId
      ? 'You'
      : (alliance.data?.members.find((m) => m.playerId === playerId)?.displayName ?? 'Ally');
  useChannelSubscription(full ? `system:${full.id}` : undefined);
  const systemName = useSystemNames(galaxyId);

  const [error, setError] = useState<unknown>(null);
  const refresh = useCallback(
    () => invalidateSoon(queryClient, [queryKeys.systems(galaxyId), queryKeys.galaxyMe(galaxyId)]),
    [queryClient, galaxyId]
  );
  const systemKey = queryKeys.system(galaxyId, systemId);
  /**
   * Instant feedback (spec §3.8): show the predicted result now, swap in the server's answer when it
   * comes, roll back on an error. The refetch that follows reconciles either way.
   */
  const optimistic = <V, R>(
    predict: (system: FullSystem, vars: V, pendingId: string, now: number) => FullSystem,
    apply: (system: FullSystem, response: R) => FullSystem
  ) => ({
    onMutate: async (vars: V): Promise<OptimisticContext> => {
      await queryClient.cancelQueries({ queryKey: systemKey });
      const snapshot = queryClient.getQueryData<SystemResponse>(systemKey);
      const pendingId = `${PENDING_ID_PREFIX}${crypto.randomUUID()}`;
      setError(null);
      patchCachedSystem(queryClient, galaxyId, systemId, (s) =>
        predict(s, vars, pendingId, Date.now())
      );
      return { snapshot, pendingId };
    },
    onSuccess: (response: R, _vars: V, context: OptimisticContext | undefined) => {
      patchCachedSystem(queryClient, galaxyId, systemId, (s) =>
        apply(context ? withoutPending(s, context.pendingId) : s, response)
      );
      refresh();
    },
    onError: (e: unknown, _vars: V, context: OptimisticContext | undefined) => {
      if (context?.snapshot) queryClient.setQueryData(systemKey, context.snapshot);
      setError(e);
      refresh();
    },
  });
  const upgrade = useMutation({
    mutationFn: (facility: FacilityType) => endpoints.upgrade(galaxyId, systemId, facility),
    ...optimistic(predictUpgradeStarted, withUpgradeStarted),
  });
  const cancel = useMutation({
    mutationFn: (itemId: string) => endpoints.cancelUpgrade(galaxyId, systemId, itemId),
    ...optimistic(predictUpgradeCancelled, withUpgradeCancelled),
  });
  const trade = useMutation({
    mutationFn: (resources: Resources) => endpoints.startPortTrade(galaxyId, systemId, resources),
    ...optimistic(predictPortTradeStarted, withPortTradeStarted),
  });
  const produce = useMutation({
    mutationFn: (order: { shipType: ShipType; quantity: number }) =>
      endpoints.startShipProduction(galaxyId, systemId, { ...order, stealth: false }),
    ...optimistic(predictProductionStarted, withProductionStarted),
  });
  const cancelProduction = useMutation({
    mutationFn: (itemId: string) => endpoints.cancelShipProduction(galaxyId, systemId, itemId),
    ...optimistic(predictProductionCancelled, withProductionCancelled),
  });
  const actions: SystemActions = {
    upgrade: (facility) => upgrade.mutate(facility),
    cancel: (itemId) => cancel.mutate(itemId),
    trade: (resources) => trade.mutate(resources),
    produce: (shipType, quantity) => produce.mutate({ shipType, quantity }),
    cancelProduction: (itemId) => cancelProduction.mutate(itemId),
    pending: {
      upgrade: upgrade.isPending ? upgrade.variables : undefined,
      cancel: cancel.isPending ? cancel.variables : undefined,
      trade: trade.isPending,
      produce: produce.isPending,
      cancelProduction: cancelProduction.isPending ? cancelProduction.variables : undefined,
    },
    error,
    ...(me && codex.data
      ? {
          finishNow: {
            galaxyId,
            darkMatter: me.darkMatter,
            darkMatterPerHour: codex.data.speedUps.darkMatterPerHour,
          },
        }
      : {}),
  };

  // Refetch shortly after the next queue item or trade is due, in case its live event is missed.
  const nextDue = full
    ? Math.min(
        ...full.buildingQueue.map((q) => Date.parse(q.completesAt)),
        ...full.shipProduction.flatMap((i) => (i.nextShipAt ? [Date.parse(i.nextShipAt)] : [])),
        full.portTrade ? Date.parse(full.portTrade.completesAt) : Number.POSITIVE_INFINITY
      )
    : Number.POSITIVE_INFINITY;
  useEffect(() => {
    if (!Number.isFinite(nextDue)) return;
    const wait = Math.min(MAX_TIMEOUT_MS, Math.max(0, nextDue - Date.now()) + 1500);
    const id = setTimeout(refresh, wait);
    return () => clearTimeout(id);
  }, [nextDue, refresh]);

  return (
    <Page width="wide">
      <div className="flex items-center justify-between gap-2">
        <Link to={`/g/${galaxyId}`} className="inline-flex items-center gap-1 text-sm text-info">
          <ArrowLeft className="size-4" aria-hidden /> Galaxy
        </Link>
        {me ? (
          <SystemSwitcher
            galaxyId={galaxyId}
            systems={me.systems}
            currentId={systemId}
            search={params.toString() ? `?${params.toString()}` : ''}
          />
        ) : null}
      </div>
      {system.isPending ? <Spinner /> : null}
      {system.error ? <ErrorNotice error={system.error} /> : null}
      {system.data ? (
        full && !allied ? (
          <OwnSystemView
            galaxyId={galaxyId}
            system={full}
            now={now}
            tab={tab}
            onTabChange={setTab}
            actions={actions}
            scouts={scouts.data?.items}
            firstGoals={me && me.systems[0]?.id === full.id ? me.firstGoals : undefined}
            alerts={
              <SystemMilitaryBar galaxyId={galaxyId} system={full} now={now} sendLink={false} />
            }
          />
        ) : (
          <>
            <header>
              <div className="flex items-center gap-2">
                <h1 className="font-display text-2xl font-bold uppercase tracking-[0.08em] text-text">
                  {system.data.system.name}
                </h1>
                {system.data.system.ownerShielded ? (
                  <Shield className="size-5 text-info" aria-label="Beginner shield" />
                ) : null}
              </div>
            </header>
            {allied ? (
              <>
                <SystemMilitaryBar galaxyId={galaxyId} system={allied} now={now} allied />
                <AlliedSystemView
                  system={allied}
                  now={now}
                  memberName={memberName}
                  owner={
                    <OwnerLinks
                      galaxyId={galaxyId}
                      owner={allied.owner}
                      myPlayerId={me?.playerId}
                    />
                  }
                />
              </>
            ) : (
              <>
                <ForeignSystemInfo
                  system={system.data.system}
                  owner={
                    <OwnerLinks
                      galaxyId={galaxyId}
                      owner={system.data.system.owner}
                      myPlayerId={me?.playerId}
                    />
                  }
                  name={systemName}
                  now={now}
                />
                <ForeignActions
                  galaxyId={galaxyId}
                  me={me}
                  systemId={system.data.system.id}
                  now={now}
                />
                {system.data.tier === 'intel' ? <IntelReport system={system.data.system} /> : null}
              </>
            )}
          </>
        )
      ) : null}
    </Page>
  );
}

export function MenuScreen() {
  const { galaxyId, me } = useOutletContext<GameContext>();
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const account = useSession((s) => s.account);
  const navigate = useNavigate();
  const { logout, pending } = useLogout();
  return (
    <Page width="narrow">
      <Panel title="Account">
        <p className="text-sm text-text">{me?.displayName}</p>
        <p className="text-sm text-muted">{account?.email}</p>
      </Panel>
      <Button
        variant="secondary"
        className="w-full"
        onClick={() => navigate(`/g/${galaxyId}/codex`)}
      >
        Codex
      </Button>
      <Button
        variant="secondary"
        className="w-full"
        disabled={!me}
        onClick={() => navigate(`/g/${galaxyId}/players/${me?.playerId}`)}
      >
        My profile
      </Button>
      <Button
        variant="secondary"
        className="w-full"
        onClick={() => navigate(`/g/${galaxyId}/empire`)}
      >
        Empire
      </Button>
      <Button
        variant="secondary"
        className="w-full"
        onClick={() => navigate(`/g/${galaxyId}/rankings`)}
      >
        Rankings
      </Button>
      <Button variant="secondary" className="w-full" onClick={() => setFeedbackOpen(true)}>
        Send feedback
      </Button>
      <DiscordLink />
      <FeedbackSheet
        open={feedbackOpen}
        onClose={() => setFeedbackOpen(false)}
        galaxyId={galaxyId}
      />
      <Button variant="secondary" className="w-full" onClick={() => navigate('/galaxies')}>
        Switch galaxy
      </Button>
      <Button variant="secondary" className="w-full" disabled={pending} onClick={logout}>
        Log out
      </Button>
      <p className="text-center text-xs text-muted">{VERSION_LABEL}</p>
    </Page>
  );
}
